import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/common/kit";
import { useProducts } from "@/components/common/Filters";

const EMPTY = { full_name: "", mobile: "", employee_code: "", username: "", password: "", joining_date: "", notes: "", status: "ACTIVE" };

export function SalesmanForm({ open, salesman, onClose, onSaved }) {
  const { bizDate } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(salesman ? { ...EMPTY, ...salesman } : { ...EMPTY, joining_date: bizDate }); }, [open, salesman, bizDate]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { full_name, mobile, employee_code, joining_date, notes, status, username, password } = f;
      const body = { full_name, mobile, employee_code, joining_date, notes, status };
      if (salesman) await api.put(`/salesmen/${salesman.id}`, body);
      else await api.post("/salesmen", { ...body, username, password });
      toast.success(salesman ? "Salesman updated" : "Salesman created");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="salesman-form-dialog">
        <DialogHeader><DialogTitle>{salesman ? `Edit ${salesman.full_name}` : "New Salesman"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Full name"><Input value={f.full_name} onChange={set("full_name")} data-testid="sm-full-name" /></Field>
          <Field label="Mobile"><Input value={f.mobile} onChange={set("mobile")} data-testid="sm-mobile" /></Field>
          <Field label="Employee code"><Input value={f.employee_code} onChange={set("employee_code")} data-testid="sm-employee-code" /></Field>
          <Field label="Joining date"><Input type="date" value={f.joining_date} onChange={set("joining_date")} data-testid="sm-joining-date" /></Field>
          {!salesman && (
            <>
              <Field label="Username / Login ID"><Input value={f.username} onChange={set("username")} autoCapitalize="none" data-testid="sm-username" /></Field>
              <Field label="Password" hint="Min 6 characters"><Input type="password" value={f.password} onChange={set("password")} data-testid="sm-password" /></Field>
            </>
          )}
          <Field label="Status">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="sm-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="INACTIVE">Inactive</SelectItem></SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2"><Field label="Notes"><Textarea value={f.notes} onChange={set("notes")} data-testid="sm-notes" /></Field></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="sm-save-button">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ salesman, onClose }) {
  const [pw, setPw] = useState("");
  const save = async () => {
    try {
      await api.post(`/salesmen/${salesman.id}/reset-password`, { password: pw });
      toast.success("Password reset");
      setPw("");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!salesman} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Reset password · {salesman?.full_name}</DialogTitle></DialogHeader>
        <Field label="New password" hint="Min 6 characters"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></Field>
        <DialogFooter><Button onClick={save} disabled={pw.length < 6} data-testid="reset-password-save">Reset password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OpeningStockDialog({ salesman, onClose, onSaved }) {
  const { bizDate } = useAuth();
  const products = useProducts(true);
  const [date, setDate] = useState(bizDate);
  const [qty, setQty] = useState({});
  useEffect(() => { if (salesman) { setQty({}); setDate(bizDate); } }, [salesman, bizDate]);
  const save = async () => {
    try {
      const items = Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([product_id, v]) => ({ product_id, qty: Number(v) }));
      await api.post(`/salesmen/${salesman.id}/opening-stock`, { date, items });
      toast.success("Initial opening stock saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const total = Object.values(qty).reduce((s, v) => s + (Number(v) || 0), 0);
  return (
    <Dialog open={!!salesman} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="opening-stock-dialog">
        <DialogHeader><DialogTitle>Initial opening stock · {salesman?.full_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Entered once for Day 1. Afterwards, opening stock is carried forward automatically from the previous closing.</p>
        <Field label="Day 1 date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="opening-date" /></Field>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 rounded border border-slate-200 px-3 py-2">
              <div><p className="text-sm font-semibold">{p.name}</p><p className="text-xs text-slate-500">{p.sku}</p></div>
              <Input type="number" min={0} className="h-9 w-28 text-right font-mono" placeholder="0" value={qty[p.id] ?? ""}
                onChange={(e) => setQty({ ...qty, [p.id]: e.target.value })} data-testid={`opening-qty-${p.sku}`} />
            </div>
          ))}
        </div>
        <DialogFooter className="items-center gap-3">
          <span className="font-mono text-sm">Total: {total} pks</span>
          <Button onClick={save} disabled={!total} data-testid="opening-save-button">Save opening stock</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
